import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import FieldClimatePanel from './FieldClimatePanel';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://169.58.21.41/api';

const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
});

const emptySensor = () => ({ name: '', type: 'Temperature', lat: '', lng: '', fieldId: '' });

// ── Styles shared across both modes ─────────────────────────────────────────
const inputStyle = {
    width: '100%', padding: '9px 11px', borderRadius: '7px',
    border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box'
};
const labelStyle = { display: 'block', fontWeight: 600, marginBottom: '5px', fontSize: '13px' };
const btnPrimary = {
    background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px',
    padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: '13px'
};
const btnSecondary = {
    background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '8px',
    padding: '10px 18px', cursor: 'pointer', fontWeight: 600, fontSize: '13px'
};
const btnGreen = {
    background: '#2d7a2d', color: '#fff', border: 'none', borderRadius: '8px',
    padding: '10px 18px', cursor: 'pointer', fontWeight: 600, fontSize: '13px'
};

// ─────────────────────────────────────────────────────────────────────────────
// MODE SELECTOR CARD
// ─────────────────────────────────────────────────────────────────────────────
const ModeSelector = ({ mode, onChange, tr }) => {
    const modes = [
        {
            id: 'swamed',
            icon: '🔑',
            title: tr ? 'SWAMED API Sağlar' : 'We Provide the API',
            desc: tr
                ? 'Karşı taraf sensör verilerini bizim endpoint\'imize gönderir. API anahtarı ve sensör ID\'leri oluşturursunuz.'
                : 'Partner pushes sensor data to our endpoint. You generate an API key and sensor IDs for them.',
        },
        {
            id: 'external',
            icon: '🌐',
            title: tr ? 'Dışarıdan API Alınır' : 'External API',
            desc: tr
                ? 'Partnerın kendi sistemi var (ör. insigh.io). Kimlik bilgileri ile bağlanıp cihaz listesini içe aktarırsınız.'
                : 'Partner has their own system (e.g. insigh.io). You connect with their credentials and import device list.',
        },
    ];

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '28px' }}>
            {modes.map(m => (
                <button
                    key={m.id}
                    onClick={() => onChange(m.id)}
                    style={{
                        textAlign: 'left', padding: '20px', borderRadius: '12px', cursor: 'pointer',
                        border: mode === m.id ? '2px solid #4f46e5' : '2px solid #e5e7eb',
                        background: mode === m.id ? '#eef2ff' : '#fff',
                        boxShadow: mode === m.id ? '0 0 0 3px rgba(79,70,229,0.15)' : 'none',
                        transition: 'all 0.15s',
                    }}
                >
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>{m.icon}</div>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: mode === m.id ? '#4338ca' : '#111827', marginBottom: '6px' }}>
                        {m.title}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.5 }}>{m.desc}</div>
                    {mode === m.id && (
                        <div style={{ marginTop: '10px', display: 'inline-block', background: '#4f46e5', color: '#fff', fontSize: '11px', fontWeight: 700, borderRadius: '20px', padding: '2px 10px' }}>
                            {tr ? 'SEÇİLİ' : 'SELECTED'}
                        </div>
                    )}
                </button>
            ))}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// EXTERNAL API MODE — insigh.io integration
// ─────────────────────────────────────────────────────────────────────────────
const ExternalApiMode = ({ tr, fieldId, onSensorsChanged, dbSensors, dbLoading, reloadDbSensors }) => {
    // ── Step tracking: 1=credentials, 2=devices discovered, 3=saved ──
    const [step, setStep] = useState(1);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Credentials form
    const [connName, setConnName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [jwtToken, setJwtToken] = useState('');
    const [authMode, setAuthMode] = useState('password');
    const [authLoading, setAuthLoading] = useState(false);

    // Discovered devices from insigh.io
    const [rawDevices, setRawDevices] = useState([]);
    const [pendingDevices, setPendingDevices] = useState([]);

    // Sensor location editing
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', lat: '', lng: '' });

    // ── Authenticate → fetch device list ──
    const handleConnect = async () => {
        if (!connName.trim()) { setError(tr ? 'Bağlantı adı zorunlu.' : 'Connection name is required.'); return; }
        if (authMode === 'password' && (!email.trim() || !password.trim())) {
            setError(tr ? 'E-posta ve şifre gerekli.' : 'Email and password are required.');
            return;
        }
        if (authMode === 'token' && !jwtToken.trim()) {
            setError(tr ? 'API token gerekli.' : 'API token is required.');
            return;
        }
        setError(null);
        setAuthLoading(true);

        try {
            let token = jwtToken.trim();

            if (authMode === 'password') {
                // POST to our backend proxy → backend calls insigh.io /mf-rproxy/tokens
                const res = await fetch(`${API_URL}/proxy/insighio/auth`, {
                    method: 'POST',
                    headers: getHeaders(),
                    body: JSON.stringify({ email: email.trim(), password })
                });
                if (!res.ok) {
                    const e = await res.json().catch(() => ({}));
                    throw new Error(e.error || (tr ? 'Kimlik doğrulama başarısız.' : 'Authentication failed.'));
                }
                const data = await res.json();
                token = data.token;
            }

            // GET /proxy/insighio/devices → backend calls /mf-rproxy/device/list
            const devRes = await fetch(`${API_URL}/proxy/insighio/devices`, {
                headers: { ...getHeaders(), 'X-Insighio-Token': token }
            });
            if (!devRes.ok) {
                const e = await devRes.json().catch(() => ({}));
                throw new Error(e.error || (tr ? 'Cihaz listesi alınamadı.' : 'Failed to fetch device list.'));
            }
            const devices = await devRes.json(); // [{id, name, key, metadata:{dataChannel}}]

            if (!Array.isArray(devices) || devices.length === 0) {
                throw new Error(tr ? 'Bu hesapta kayıtlı cihaz bulunamadı.' : 'No devices found on this account.');
            }

            // Fetch last measurement per device to show live values
            const enriched = await Promise.all(devices.map(async (d) => {
                const channel = d.metadata?.dataChannel || '';
                let lastMeasurements = [];
                if (channel) {
                    try {
                        const mRes = await fetch(
                            `${API_URL}/proxy/insighio/lastMeasurement?deviceId=${d.id}&channel=${channel}`,
                            { headers: { ...getHeaders(), 'X-Insighio-Token': token } }
                        );
                        if (mRes.ok) lastMeasurements = await mRes.json();
                    } catch { /* silent */ }
                }
                return {
                    id: d.id,
                    name: d.name,
                    channel,
                    deviceKey: d.key,
                    measurements: lastMeasurements,
                    lat: '',
                    lng: '',
                    selected: true,
                    _token: token,
                };
            }));

            setRawDevices(enriched);
            setPendingDevices(enriched);
            setStep(2);
        } catch (e) {
            setError(e.message);
        } finally {
            setAuthLoading(false);
        }
    };

    const [saveLoading, setSaveLoading] = useState(false);

    // ── Save connection → writes sensors to SWAMED DB ──
    const handleSave = async () => {
        const selected = pendingDevices.filter(d => d.selected);
        if (selected.length === 0) {
            setError(tr ? 'En az bir cihaz seçin.' : 'Select at least one device.');
            return;
        }
        setError(null);
        setSaveLoading(true);
        try {
            const token = selected[0]?._token;
            const res = await fetch(`${API_URL}/proxy/insighio/import`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                    token,
                    fieldId: fieldId || null,
                    devices: selected.map(d => ({
                        id: d.id,
                        name: d.name,
                        channel: d.channel,
                        lat: d.lat,
                        lng: d.lng,
                        measurements: d.measurements,
                    })),
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Import failed');

            setStep(1);
            setConnName(''); setEmail(''); setPassword(''); setJwtToken('');
            setPendingDevices([]); setRawDevices([]);
            setSuccess(
                tr
                    ? `✅ ${data.count} sensör SWAMED veritabanına kaydedildi. Veriler her 15 dakikada bir otomatik çekilecek.`
                    : `✅ ${data.count} sensors saved to SWAMED database. Data will be pulled automatically every 15 minutes.`
            );
            reloadDbSensors();
            if (onSensorsChanged) onSensorsChanged();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaveLoading(false);
        }
    };

    const startEditSensor = (sensor) => {
        setEditingId(sensor.id);
        setEditForm({ name: sensor.name, lat: sensor.lat ?? '', lng: sensor.lng ?? '' });
    };

    const handleSaveSensorEdit = async (sensorId) => {
        setError(null);
        try {
            const res = await fetch(`${API_URL}/sensors/${sensorId}`, {
                method: 'PUT', headers: getHeaders(),
                body: JSON.stringify({
                    name: editForm.name,
                    lat: parseFloat(editForm.lat),
                    lng: parseFloat(editForm.lng),
                }),
            });
            if (!res.ok) throw new Error();
            setEditingId(null);
            reloadDbSensors();
            if (onSensorsChanged) onSensorsChanged();
        } catch {
            setError(tr ? 'Sensör güncellenemedi.' : 'Failed to update sensor.');
        }
    };

    const handleDeleteDbSensor = async (sensor) => {
        if (!window.confirm(tr ? `"${sensor.name}" silinsin mi?` : `Delete "${sensor.name}"?`)) return;
        setError(null);
        try {
            const res = await fetch(`${API_URL}/sensors/${sensor.id}`, { method: 'DELETE', headers: getHeaders() });
            if (!res.ok) throw new Error();
            reloadDbSensors();
            if (onSensorsChanged) onSensorsChanged();
        } catch {
            setError(tr ? 'Sensör silinemedi.' : 'Failed to delete sensor.');
        }
    };

    const updatePending = (idx, field, val) =>
        setPendingDevices(prev => prev.map((d, i) => i === idx ? { ...d, [field]: val } : d));

    const latestVal = (measurements, nameHint) => {
        if (!measurements?.length) return null;
        const m = measurements.find(m => m.name?.toLowerCase().includes(nameHint.toLowerCase()));
        return m ? (m.value ?? m.stringValue) : null;
    };

    return (
        <div>
            {/* Alerts */}
            {error && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#991b1b', fontSize: '13px' }}>{error}</div>}
            {success && <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#065f46', fontSize: '13px' }}>{success}</div>}

            {/* ── STEP INDICATOR ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24 }}>
                {[
                    { n: 1, label: tr ? 'Kimlik Bilgileri' : 'Credentials' },
                    { n: 2, label: tr ? 'Cihazları Seç' : 'Select Devices' },
                    { n: 3, label: tr ? 'Tamamlandı' : 'Done' },
                ].map((s, i) => (
                    <React.Fragment key={s.n}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{
                                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: 13,
                                background: step >= s.n ? '#4f46e5' : '#e5e7eb',
                                color: step >= s.n ? '#fff' : '#9ca3af',
                            }}>{s.n}</div>
                            <span style={{ fontSize: 12, fontWeight: 600, color: step >= s.n ? '#4f46e5' : '#9ca3af' }}>{s.label}</span>
                        </div>
                        {i < 2 && <div style={{ flex: 1, height: 2, background: step > s.n ? '#4f46e5' : '#e5e7eb', margin: '0 8px' }} />}
                    </React.Fragment>
                ))}
            </div>

            {/* ── STEP 1: Credentials ── */}
            {step === 1 && (
                <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🌐</div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 15 }}>insigh.io {tr ? 'Bağlantısı' : 'Connection'}</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>console.insigh.io · REST API</div>
                        </div>
                    </div>

                    {/* Connection name */}
                    <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>{tr ? 'Bağlantı Adı *' : 'Connection Name *'}</label>
                        <input type="text" value={connName} onChange={e => setConnName(e.target.value)}
                            placeholder={tr ? 'Ör: Yunan Ekibi – insigh.io' : 'e.g. Greek Team – insigh.io'}
                            style={inputStyle} />
                    </div>

                    {/* Auth mode toggle */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                        <button type="button"
                            onClick={() => setAuthMode('password')}
                            style={{ ...btnSecondary, padding: '7px 14px', fontSize: 12, background: authMode === 'password' ? '#4f46e5' : '#e5e7eb', color: authMode === 'password' ? '#fff' : '#374151' }}>
                            🔐 {tr ? 'E-posta / Şifre' : 'Email / Password'}
                        </button>
                        <button type="button"
                            onClick={() => setAuthMode('token')}
                            style={{ ...btnSecondary, padding: '7px 14px', fontSize: 12, background: authMode === 'token' ? '#4f46e5' : '#e5e7eb', color: authMode === 'token' ? '#fff' : '#374151' }}>
                            🗝 {tr ? 'API Token' : 'API Token'}
                        </button>
                    </div>

                    {authMode === 'password' ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                            <div>
                                <label style={labelStyle}>{tr ? 'E-posta *' : 'Email *'}</label>
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                                    placeholder="user@example.com" style={inputStyle} autoComplete="off" />
                            </div>
                            <div>
                                <label style={labelStyle}>{tr ? 'Şifre *' : 'Password *'}</label>
                                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                                    placeholder="••••••••" style={inputStyle} autoComplete="new-password" />
                            </div>
                        </div>
                    ) : (
                        <div style={{ marginBottom: 16 }}>
                            <label style={labelStyle}>JWT / API Token *</label>
                            <input type="password" value={jwtToken} onChange={e => setJwtToken(e.target.value)}
                                placeholder="eyJhbGci..." style={inputStyle} autoComplete="new-password" />
                            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                                {tr ? 'insigh.io konsolundan aldığınız kalıcı API token\'ını yapıştırın.' : 'Paste the permanent API token from your insigh.io console.'}
                            </div>
                        </div>
                    )}

                    {/* How-to info */}
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12.5, color: '#1e40af' }}>
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>ℹ️ {tr ? 'Nasıl çalışır?' : 'How it works?'}</div>
                        <ol style={{ margin: 0, paddingLeft: 16, lineHeight: 1.7 }}>
                            <li>{tr ? 'Bağlan\'a tıklayın → insigh.io\'dan JWT token alınır' : 'Click Connect → JWT token is fetched from insigh.io'}</li>
                            <li>{tr ? 'Hesabınızdaki tüm cihazlar otomatik listelenir' : 'All devices on your account are listed automatically'}</li>
                            <li>{tr ? 'SWAMED\'e eklemek istediklerinizi seçin, koordinat girin' : 'Select which ones to add to SWAMED, enter coordinates'}</li>
                            <li>{tr ? 'Kaydet → veriler periyodik olarak çekilir' : 'Save → data is pulled periodically'}</li>
                        </ol>
                    </div>

                    <button onClick={handleConnect} disabled={authLoading}
                        style={{ ...btnPrimary, opacity: authLoading ? 0.7 : 1, minWidth: 180 }}>
                        {authLoading
                            ? `⏳ ${tr ? 'Bağlanıyor…' : 'Connecting…'}`
                            : `🔌 ${tr ? 'Bağlan & Cihazları Getir' : 'Connect & Fetch Devices'}`}
                    </button>
                </div>
            )}

            {/* ── STEP 2: Device selection ── */}
            {step === 2 && (
                <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 15 }}>
                                ✅ {rawDevices.length} {tr ? 'cihaz bulundu' : 'devices found'} — {connName}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                                {tr
                                    ? 'SWAMED\'e eklemek istediklerinizi seçin. Koordinat girmeniz gerekiyor (haritada gösterilmesi için).'
                                    : 'Select which devices to add to SWAMED. Enter coordinates to show them on the map.'}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" onClick={() => setPendingDevices(p => p.map(d => ({ ...d, selected: true })))}
                                style={{ ...btnSecondary, padding: '5px 12px', fontSize: 12 }}>
                                {tr ? 'Tümünü Seç' : 'Select All'}
                            </button>
                            <button type="button" onClick={() => setPendingDevices(p => p.map(d => ({ ...d, selected: false })))}
                                style={{ ...btnSecondary, padding: '5px 12px', fontSize: 12 }}>
                                {tr ? 'Tümünü Kaldır' : 'None'}
                            </button>
                        </div>
                    </div>

                    <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#1e293b', color: '#94a3b8', zIndex: 1 }}>
                                <tr>
                                    <th style={{ padding: '9px 10px', textAlign: 'center', width: 36 }}>✓</th>
                                    <th style={{ padding: '9px 10px', textAlign: 'left' }}>{tr ? 'Cihaz Adı' : 'Device Name'}</th>
                                    <th style={{ padding: '9px 10px', textAlign: 'left' }}>{tr ? 'Son Ölçümler' : 'Last Measurements'}</th>
                                    <th style={{ padding: '9px 10px', textAlign: 'left' }}>{tr ? 'Enlem *' : 'Latitude *'}</th>
                                    <th style={{ padding: '9px 10px', textAlign: 'left' }}>{tr ? 'Boylam *' : 'Longitude *'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pendingDevices.map((d, idx) => (
                                    <tr key={d.id} style={{ borderBottom: '1px solid #f1f5f9', background: d.selected ? '#fff' : '#fafafa' }}>
                                        <td style={{ textAlign: 'center', padding: '8px 10px' }}>
                                            <input type="checkbox" checked={d.selected}
                                                onChange={() => updatePending(idx, 'selected', !d.selected)} />
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                            <div style={{ fontWeight: 700, fontSize: 12.5 }}>{d.name}</div>
                                            <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#94a3b8' }}>{d.id.slice(0, 16)}…</div>
                                        </td>
                                        <td style={{ padding: '6px 8px', fontSize: 12 }}>
                                            {d.measurements?.length > 0 ? (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                    {d.measurements.slice(0, 5).map((m, mi) => (
                                                        <span key={mi} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, padding: '1px 6px', fontSize: 11, fontFamily: 'monospace' }}>
                                                            {m.name?.split('-').pop()}: {m.value != null ? Number(m.value).toFixed(2) : m.stringValue ?? '—'}
                                                            {m.unit ? ` ${m.unit}` : ''}
                                                        </span>
                                                    ))}
                                                    {d.measurements.length > 5 && (
                                                        <span style={{ fontSize: 11, color: '#94a3b8' }}>+{d.measurements.length - 5} more</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span style={{ color: '#9ca3af', fontSize: 11 }}>{tr ? 'Ölçüm yok' : 'No measurements'}</span>
                                            )}
                                        </td>
                                        <td style={{ padding: '4px 6px' }}>
                                            <input type="number" value={d.lat} step="any" placeholder="36.5500"
                                                onChange={e => updatePending(idx, 'lat', e.target.value)}
                                                style={{ ...inputStyle, padding: '5px 8px', fontSize: 12, width: 100 }} />
                                        </td>
                                        <td style={{ padding: '4px 6px' }}>
                                            <input type="number" value={d.lng} step="any" placeholder="32.1200"
                                                onChange={e => updatePending(idx, 'lng', e.target.value)}
                                                style={{ ...inputStyle, padding: '5px 8px', fontSize: 12, width: 100 }} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, marginBottom: 16 }}>
                        {pendingDevices.filter(d => d.selected).length} / {pendingDevices.length} {tr ? 'cihaz seçili' : 'devices selected'}
                        {' · '}
                        <span style={{ color: '#ef4444' }}>
                            {tr ? '* Koordinat zorunlu değil ama haritada gösterilmek için gerekli.' : '* Coordinates optional but required to show on map.'}
                        </span>
                    </div>

                    <div style={{ display: 'flex', gap: 10 }}>
                        <button onClick={handleSave} disabled={saveLoading}
                            style={{ ...btnGreen, opacity: saveLoading ? 0.7 : 1 }}>
                            {saveLoading
                                ? `⏳ ${tr ? 'Kaydediliyor…' : 'Saving…'}`
                                : `💾 ${tr ? 'SWAMED\'e Kaydet' : 'Save to SWAMED'}`}
                        </button>
                        <button onClick={() => { setStep(1); setError(null); }} style={btnSecondary}>
                            ← {tr ? 'Geri' : 'Back'}
                        </button>
                    </div>
                </div>
            )}

            {/* ── DB Sensors from insigh.io ── */}
            {step === 1 && (
                dbLoading ? (
                    <div style={{ textAlign: 'center', padding: '30px 20px', color: '#9ca3af', fontSize: 13 }}>
                        ⏳ {tr ? 'Sensörler yükleniyor…' : 'Loading sensors…'}
                    </div>
                ) : dbSensors.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
                        <div style={{ fontSize: 48, marginBottom: 12 }}>🌐</div>
                        <p style={{ margin: 0, fontSize: 14 }}>
                            {tr ? 'Henüz insigh.io sensörü yok. Yukarıdaki formu doldurun.' : 'No insigh.io sensors yet. Fill in the form above.'}
                        </p>
                    </div>
                ) : (
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#374151', marginBottom: 12 }}>
                            📋 {tr ? 'insigh.io Sensörleri' : 'insigh.io Sensors'} ({dbSensors.length})
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {dbSensors.map(s => (
                                <div key={s.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                    {editingId === s.id ? (
                                        /* ── Inline edit form ── */
                                        <div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                                                <div>
                                                    <label style={{ ...labelStyle, fontSize: 12 }}>{tr ? 'Sensör Adı' : 'Sensor Name'}</label>
                                                    <input type="text" value={editForm.name}
                                                        onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                                                        style={{ ...inputStyle, padding: '6px 9px', fontSize: 12 }} />
                                                </div>
                                                <div>
                                                    <label style={{ ...labelStyle, fontSize: 12 }}>{tr ? 'Enlem' : 'Latitude'}</label>
                                                    <input type="number" step="any" value={editForm.lat}
                                                        onChange={e => setEditForm(f => ({ ...f, lat: e.target.value }))}
                                                        style={{ ...inputStyle, padding: '6px 9px', fontSize: 12 }} />
                                                </div>
                                                <div>
                                                    <label style={{ ...labelStyle, fontSize: 12 }}>{tr ? 'Boylam' : 'Longitude'}</label>
                                                    <input type="number" step="any" value={editForm.lng}
                                                        onChange={e => setEditForm(f => ({ ...f, lng: e.target.value }))}
                                                        style={{ ...inputStyle, padding: '6px 9px', fontSize: 12 }} />
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <button onClick={() => handleSaveSensorEdit(s.id)}
                                                    style={{ ...btnGreen, padding: '6px 14px', fontSize: 12 }}>
                                                    ✓ {tr ? 'Kaydet' : 'Save'}
                                                </button>
                                                <button onClick={() => setEditingId(null)}
                                                    style={{ ...btnSecondary, padding: '6px 12px', fontSize: 12 }}>
                                                    {tr ? 'İptal' : 'Cancel'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        /* ── Read view ── */
                                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                                            <div style={{ flex: 1 }}>
                                                {/* Header row */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                                                    <span style={{ fontWeight: 700, fontSize: 14 }}>{s.name}</span>
                                                    <span style={{ background: '#dbeafe', color: '#1e40af', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>insigh.io</span>
                                                    <span style={{ background: s.status === 'ACTIVE' ? '#d1fae5' : '#f3f4f6', color: s.status === 'ACTIVE' ? '#065f46' : '#6b7280', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>
                                                        {s.status === 'ACTIVE' ? '● ACTIVE' : '○ ' + s.status}
                                                    </span>
                                                </div>
                                                {/* Location + sync row */}
                                                <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#6b7280', flexWrap: 'wrap', marginBottom: 8 }}>
                                                    {s.lat && s.lng && (Number(s.lat) !== 0 || Number(s.lng) !== 0) ? (
                                                        <span>📍 {Number(s.lat).toFixed(5)}, {Number(s.lng).toFixed(5)}</span>
                                                    ) : (
                                                        <span style={{ color: '#ef4444' }}>⚠ {tr ? 'Koordinat girilmemiş' : 'No coordinates'}</span>
                                                    )}
                                                    {s.insighioLastSync && (
                                                        <span>🔄 {new Date(s.insighioLastSync).toLocaleString()}</span>
                                                    )}
                                                    <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#94a3b8' }}>ID: {(s.insighioDeviceId || '').slice(0, 16)}…</span>
                                                </div>
                                                {/* Measurement chips */}
                                                {s.latestData && typeof s.latestData === 'object' && Object.keys(s.latestData).filter(k => !k.endsWith('_unit')).length > 0 ? (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                                        {Object.entries(s.latestData)
                                                            .filter(([k]) => !k.endsWith('_unit'))
                                                            .map(([k, v]) => (
                                                                <span key={k} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 5, padding: '3px 8px', fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                                                    <span style={{ color: '#64748b' }}>{k}: </span>
                                                                    <strong>{typeof v === 'number' ? v.toFixed(2) : v}</strong>
                                                                    {s.latestData[`${k}_unit`] ? <span style={{ color: '#94a3b8' }}> {s.latestData[`${k}_unit`]}</span> : ''}
                                                                </span>
                                                            ))}
                                                    </div>
                                                ) : (
                                                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{tr ? 'Ölçüm verisi bekleniyor (15 dakikada bir güncellenir)' : 'Waiting for measurements (updated every 15 min)'}</span>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                                <button onClick={() => startEditSensor(s)}
                                                    style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#1e40af' }}>
                                                    ✏️ {tr ? 'Düzenle' : 'Edit'}
                                                </button>
                                                <button onClick={() => handleDeleteDbSensor(s)}
                                                    style={{ background: '#fee2e2', border: 'none', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#991b1b' }}>
                                                    🗑
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )
            )}

            {/* ── API Reference ── */}
            <details style={{ marginTop: 28 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 13, color: '#4f46e5', padding: '10px 0' }}>
                    📖 insigh.io API {tr ? 'Referansı' : 'Reference'}
                </summary>
                <div style={{ background: '#0f172a', borderRadius: 10, padding: '14px 16px', fontFamily: 'monospace', fontSize: 12, color: '#e2e8f0', marginTop: 8, overflowX: 'auto' }}>
                    <pre style={{ margin: 0 }}>{`# 1. Authenticate
POST https://console.insigh.io/mf-rproxy/tokens
Body: { "email": "...", "password": "..." }
→ { "token": "eyJ..." }

# 2. List all devices
GET  https://console.insigh.io/mf-rproxy/device/list
Auth: Authorization: <token>
→ [{ "id":"...", "name":"...", "metadata":{"dataChannel":"..."} }]

# 3. Last measurement per device
GET  https://console.insigh.io/mf-rproxy/device/lastMeasurement
     ?channel=<dataChannel>&id=<deviceId>
→ [{ "name":"board_humidity", "value": 24.04, "unit":"%RH", "time":"..." }]

# 4. Historical data (single metric)
GET  https://console.insigh.io/mf-rproxy/measurement/query
     ?name=<metric>&channel=<ch>&publisher=<deviceId>&duration=7d
→ { "name":"...", "values":[{"time":"...","value":...}] }

# 5. Historical data (all metrics)
GET  https://console.insigh.io/mf-rproxy/measurement/queryPack
     ?channel=<ch>&publisher=<deviceId>&startRange=<ISO>&endRange=<ISO>
→ [{ "time":"...", "temp":8.62, "indoor_rh":108.11, ... }]`}
                    </pre>
                </div>
            </details>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// SWAMED API MODE (existing system — we provide the key)
// ─────────────────────────────────────────────────────────────────────────────
const SwamEdApiMode = ({ tr, onSensorsChanged, fields = [], defaultFieldId = '' }) => {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: '', description: '' });
    const [sensorDefs, setSensorDefs] = useState([{ ...emptySensor(), fieldId: defaultFieldId }]);
    const [newResult, setNewResult] = useState(null);
    const [addingSensorToKey, setAddingSensorToKey] = useState(null);
    const [addSensorForm, setAddSensorForm] = useState({ ...emptySensor(), fieldId: defaultFieldId });
    const [editingSensor, setEditingSensor] = useState(null);
    const [editSensorForm, setEditSensorForm] = useState({ name: '', type: 'Temperature', lat: '', lng: '', fieldId: defaultFieldId });
    const [copiedText, setCopiedText] = useState(null);
    const [visibleKeys, setVisibleKeys] = useState({});

    useEffect(() => {
        if (!defaultFieldId) return;
        setSensorDefs(prev => prev.map(s => s.fieldId ? s : { ...s, fieldId: defaultFieldId }));
        setAddSensorForm(prev => prev.fieldId ? prev : { ...prev, fieldId: defaultFieldId });
    }, [defaultFieldId]);

    const L = {
        newBtn:       tr ? '+ Yeni Entegrasyon'                : '+ New Integration',
        formTitle:    tr ? 'Yeni Entegrasyon Oluştur'          : 'Create New Integration',
        nameLabel:    tr ? 'Kurum / Entegrasyon Adı *'         : 'Organization / Integration Name *',
        namePh:       tr ? 'Örn: TU Berlin – Almanya Ağı'      : 'e.g. TU Berlin – Germany Network',
        descLabel:    tr ? 'Açıklama (isteğe bağlı)'           : 'Description (optional)',
        descPh:       tr ? '5 toprak nemi sensörü'             : '5 soil moisture sensors in Alanya',
        sensorSec:    tr ? '📍 Sensör Tanımları'               : '📍 Sensor Definitions',
        sensorHint:   tr ? 'Her sensör için konum girin.'      : 'Enter location for each sensor.',
        addSensorBtn: tr ? '+ Sensör Ekle'                     : '+ Add Sensor',
        sensorNamePh: tr ? 'Sensör adı (Örn: Alanya-1)'       : 'Sensor name (e.g. Alanya-1)',
        latPh:        tr ? 'Enlem'                             : 'Latitude',
        lngPh:        tr ? 'Boylam'                            : 'Longitude',
        createBtn:    tr ? 'Oluştur'                           : 'Create',
        cancelBtn:    tr ? 'İptal'                             : 'Cancel',
        saveBtn:      tr ? 'Kaydet'                            : 'Save',
        editBtn:      tr ? '✏️ Düzenle'                        : '✏️ Edit',
        deleteBtn:    tr ? '🗑 Sil'                            : '🗑 Delete',
        copyId:       tr ? 'ID Kopyala'                        : 'Copy ID',
        active:       tr ? 'AKTİF'                             : 'ACTIVE',
        inactive:     tr ? 'KAPALI'                            : 'INACTIVE',
        pause:        tr ? '⏸ Durdur'                         : '⏸ Pause',
        activate:     tr ? '▶ Aktive Et'                       : '▶ Activate',
        rotate:       tr ? '🔄 Yenile'                         : '🔄 Rotate',
        deleteKey:    tr ? '🗑 Sil'                            : '🗑 Delete',
        sensors:      tr ? 'sensör'                            : 'sensor(s)',
        linked:       tr ? '📍 Bağlı Sensörler'               : '📍 Linked Sensors',
        addMore:      tr ? '+ Sensör Ekle'                     : '+ Add Sensor',
        created:      tr ? 'Oluşturulma:'                      : 'Created:',
        lastUsed:     tr ? 'Son kullanım:'                     : 'Last used:',
        loading:      tr ? 'Yükleniyor...'                     : 'Loading...',
        empty:        tr ? 'Henüz entegrasyon yok.'            : 'No integrations yet. Click "+ New Integration".',
        errLoad:      tr ? 'API anahtarları yüklenemedi.'      : 'Failed to load API keys.',
        errCreate:    tr ? 'Entegrasyon oluşturulamadı.'       : 'Failed to create integration.',
        errToggle:    tr ? 'Durum güncellenemedi.'             : 'Failed to update status.',
        errDelete:    tr ? 'Silinemedi.'                       : 'Failed to delete.',
        errRotate:    tr ? 'Anahtar yenilenemedi.'             : 'Failed to rotate key.',
        errEdit:      tr ? 'Sensör güncellenemedi.'            : 'Failed to update sensor.',
        errAddSensor: tr ? 'Sensör eklenemedi.'               : 'Failed to add sensor.',
        errDelSensor: tr ? 'Sensör silinemedi.'               : 'Failed to delete sensor.',
        okEdit:       tr ? 'Sensör güncellendi.'               : 'Sensor updated.',
        okAdd:        tr ? 'Sensör eklendi.'                   : 'Sensor added.',
        okDelete:     tr ? 'Entegrasyon silindi.'              : 'Integration deleted.',
        okRotate:     tr ? 'Anahtar yenilendi.'                : 'Key rotated. Copy the new value.',
        okDelSensor:  tr ? 'Sensör silindi.'                   : 'Sensor deleted.',
        deleteConfirm: (name) => tr ? `"${name}" silinsin mi?` : `Delete "${name}"?`,
        rotateConfirm: (name) => tr ? `"${name}" için yeni anahtar üretilecek.` : `Generate new key for "${name}"? The old key will stop working.`,
        sensorDeleteConfirm: (name) => tr ? `"${name}" sensörü silinsin mi?` : `Delete sensor "${name}"?`,
    };

    useEffect(() => { fetchKeys(); }, []);

    const fetchKeys = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/apikeys`, { headers: getHeaders() });
            if (!res.ok) throw new Error();
            setKeys(await res.json());
        } catch {
            setError(L.errLoad);
        } finally {
            setLoading(false);
        }
    };

    const clearMessages = () => { setError(null); setSuccess(null); };

    const handleCreate = async (e) => {
        e.preventDefault();
        clearMessages();
        const validSensors = sensorDefs.filter(s => s.name && s.lat !== '' && s.lng !== '');
        try {
            const res = await fetch(`${API_URL}/apikeys`, {
                method: 'POST', headers: getHeaders(),
                body: JSON.stringify({ ...form, sensors: validSensors })
            });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setKeys(prev => [data, ...prev]);
            setNewResult(data);
            setForm({ name: '', description: '' });
            setSensorDefs([{ ...emptySensor(), fieldId: defaultFieldId }]);
            setShowForm(false);
            if (onSensorsChanged) onSensorsChanged();
        } catch { setError(L.errCreate); }
    };

    const handleToggle = async (key) => {
        clearMessages();
        try {
            const res = await fetch(`${API_URL}/apikeys/${key.id}`, {
                method: 'PUT', headers: getHeaders(),
                body: JSON.stringify({ isActive: !key.isActive })
            });
            if (!res.ok) throw new Error();
            const updated = await res.json();
            setKeys(prev => prev.map(k => k.id === updated.id ? updated : k));
        } catch { setError(L.errToggle); }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(L.deleteConfirm(name))) return;
        clearMessages();
        try {
            const res = await fetch(`${API_URL}/apikeys/${id}`, { method: 'DELETE', headers: getHeaders() });
            if (!res.ok) throw new Error();
            setKeys(prev => prev.filter(k => k.id !== id));
            setSuccess(L.okDelete);
        } catch { setError(L.errDelete); }
    };

    const handleRotate = async (id, name) => {
        if (!window.confirm(L.rotateConfirm(name))) return;
        clearMessages();
        try {
            const res = await fetch(`${API_URL}/apikeys/${id}/rotate`, { method: 'POST', headers: getHeaders() });
            if (!res.ok) throw new Error();
            const updated = await res.json();
            setKeys(prev => prev.map(k => k.id === updated.id ? updated : k));
            setVisibleKeys(prev => ({ ...prev, [id]: true }));
            setSuccess(L.okRotate);
        } catch { setError(L.errRotate); }
    };

    const handleEditSensor = async () => {
        if (!editingSensor) return;
        clearMessages();
        try {
            const res = await fetch(`${API_URL}/sensors/${editingSensor.sensor.id}`, {
                method: 'PUT', headers: getHeaders(),
                body: JSON.stringify({ name: editSensorForm.name, type: editSensorForm.type, lat: parseFloat(editSensorForm.lat), lng: parseFloat(editSensorForm.lng), fieldId: editSensorForm.fieldId ? Number(editSensorForm.fieldId) : null })
            });
            if (!res.ok) throw new Error();
            const updated = await res.json();
            setKeys(prev => prev.map(k => {
                if (k.id !== editingSensor.keyId) return k;
                return { ...k, sensors: k.sensors.map(s => s.id === updated.id ? updated : s) };
            }));
            setEditingSensor(null);
            setSuccess(L.okEdit);
            if (onSensorsChanged) onSensorsChanged();
        } catch { setError(L.errEdit); }
    };

    const handleDeleteSensor = async (keyId, sensor) => {
        if (!window.confirm(L.sensorDeleteConfirm(sensor.name))) return;
        clearMessages();
        try {
            const res = await fetch(`${API_URL}/sensors/${sensor.id}`, { method: 'DELETE', headers: getHeaders() });
            if (!res.ok) throw new Error();
            setKeys(prev => prev.map(k => {
                if (k.id !== keyId) return k;
                return { ...k, sensors: k.sensors.filter(s => s.id !== sensor.id) };
            }));
            setSuccess(L.okDelSensor);
            if (onSensorsChanged) onSensorsChanged();
        } catch { setError(L.errDelSensor); }
    };

    const handleAddSensor = async (keyId) => {
        clearMessages();
        try {
            const res = await fetch(`${API_URL}/apikeys/${keyId}/sensors`, {
                method: 'POST', headers: getHeaders(),
                body: JSON.stringify(addSensorForm)
            });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setKeys(prev => prev.map(k => {
                if (k.id !== keyId) return k;
                return { ...k, sensors: [...(k.sensors || []), data.sensor] };
            }));
            setAddingSensorToKey(null);
            setAddSensorForm({ ...emptySensor(), fieldId: defaultFieldId });
            setSuccess(L.okAdd);
            if (onSensorsChanged) onSensorsChanged();
        } catch { setError(L.errAddSensor); }
    };

    const copy = (text, label) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedText(label);
            setTimeout(() => setCopiedText(null), 2000);
        });
    };

    const maskKey = (k) => `${k.slice(0, 8)}${'•'.repeat(16)}${k.slice(-4)}`;
    const fmt = (d) => d ? new Date(d).toLocaleString() : '—';

    const updateSensorDef = (idx, field, value) => setSensorDefs(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
    const removeSensorDef = (idx) => setSensorDefs(prev => prev.filter((_, i) => i !== idx));
    const addSensorDef = () => setSensorDefs(prev => [...prev, { ...emptySensor(), fieldId: defaultFieldId }]);

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>
                        🔑 {tr ? 'SWAMED API Entegrasyonları' : 'SWAMED API Integrations'}
                    </h3>
                    <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '13px' }}>
                        {tr
                            ? 'Karşı tarafa API anahtarı ve sensör ID\'leri verin. Verilerini bizim endpoint\'imize gönderirler.'
                            : 'Provide the partner with an API key and sensor IDs. They push data to our endpoint.'}
                    </p>
                </div>
                <button onClick={() => { setShowForm(true); clearMessages(); }} style={btnGreen}>
                    {L.newBtn}
                </button>
            </div>

            {error && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#991b1b', fontSize: '13px' }}>{error}</div>}
            {success && <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#065f46', fontSize: '13px' }}>{success}</div>}

            {/* Result modal after creation */}
            {newResult && (
                <div style={{ background: '#f0fff4', border: '2px solid #38a169', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <h3 style={{ margin: 0, color: '#276749', fontSize: '15px' }}>✅ {tr ? 'Entegrasyon Oluşturuldu:' : 'Integration Created:'} {newResult.name}</h3>
                        <button onClick={() => setNewResult(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666' }}>✕</button>
                    </div>
                    <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#555' }}>
                        {tr ? 'Bu bilgileri karşı tarafa gönderin. API anahtarı bir daha tam gösterilmeyecek.' : 'Share this with the partner. The API key will not be shown in full again.'}
                    </p>
                    <div style={{ background: '#1e293b', borderRadius: '8px', padding: '16px', fontFamily: 'monospace', fontSize: '13px', color: '#e2e8f0', marginBottom: '12px' }}>
                        <div style={{ marginBottom: '8px' }}>
                            <span style={{ color: '#94a3b8' }}>API Key: </span>
                            <span style={{ color: '#86efac', fontWeight: 700 }}>{newResult.key}</span>
                            <button onClick={() => copy(newResult.key, 'key')} style={{ marginLeft: '12px', background: copiedText === 'key' ? '#276749' : '#334155', color: '#fff', border: 'none', borderRadius: '6px', padding: '3px 10px', cursor: 'pointer', fontSize: '12px' }}>
                                {copiedText === 'key' ? '✓' : tr ? 'Kopyala' : 'Copy'}
                            </button>
                        </div>
                        <div style={{ color: '#94a3b8', marginBottom: '6px' }}>Endpoint: <span style={{ color: '#7dd3fc' }}>POST http://169.58.21.41/api/ingest/sensor-data</span></div>
                        {newResult.sensors && newResult.sensors.length > 0 && (
                            <div>
                                <div style={{ color: '#94a3b8', marginBottom: '4px' }}>Sensor IDs:</div>
                                {newResult.sensors.map(s => (
                                    <div key={s.id} style={{ paddingLeft: '16px', marginBottom: '2px' }}>
                                        <span style={{ color: '#fbbf24' }}>#{s.id}</span>
                                        <span style={{ color: '#e2e8f0' }}> — {s.name} [{s.lat}, {s.lng}]</span>
                                        <button onClick={() => copy(String(s.id), `sid-${s.id}`)} style={{ marginLeft: '10px', background: copiedText === `sid-${s.id}` ? '#276749' : '#334155', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }}>
                                            {copiedText === `sid-${s.id}` ? '✓' : 'Copy ID'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <details style={{ marginTop: '8px' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '13px', color: '#276749' }}>
                            {tr ? 'Python örnek kodu göster' : 'Show Python example'}
                        </summary>
                        <div style={{ background: '#1e293b', borderRadius: '8px', padding: '14px', fontFamily: 'monospace', fontSize: '12px', color: '#e2e8f0', marginTop: '8px', overflowX: 'auto' }}>
                            <pre style={{ margin: 0 }}>{`import requests

API_KEY = "${newResult.key}"
URL = "http://169.58.21.41/api/ingest/sensor-data"

${(newResult.sensors || []).map(s => `# ${s.name} — Sensor #${s.id}
requests.post(URL, headers={"X-API-Key": API_KEY}, json={
    "sensorId": ${s.id},
    "ndvi": 0.65, "ndmi": 0.31, "etc": 3.57,
    "soilMoisture": 42.0, "ph": 6.8, "ec": 0.45,
    "dissolvedO2": 7.2, "turbidity": 3.5,
    "source": "external"
})`).join('\n\n')}`}
                            </pre>
                        </div>
                    </details>
                </div>
            )}

            {/* Create Form */}
            {showForm && (
                <div style={{ background: '#f8f9fa', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '22px', marginBottom: '24px' }}>
                    <h3 style={{ margin: '0 0 18px', fontSize: '15px', fontWeight: 700 }}>{L.formTitle}</h3>
                    <form onSubmit={handleCreate}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                            <div>
                                <label style={labelStyle}>{L.nameLabel}</label>
                                <input type="text" placeholder={L.namePh} value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required style={inputStyle} />
                            </div>
                            <div>
                                <label style={labelStyle}>{L.descLabel}</label>
                                <input type="text" placeholder={L.descPh} value={form.description}
                                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={inputStyle} />
                            </div>
                        </div>

                        <div style={{ marginBottom: '18px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                <label style={{ fontWeight: 700, fontSize: '14px' }}>{L.sensorSec}</label>
                                <button type="button" onClick={addSensorDef}
                                    style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '13px', color: '#2e7d32', fontWeight: 600 }}>
                                    {L.addSensorBtn}
                                </button>
                            </div>
                            <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#888' }}>{L.sensorHint}</p>
                            {sensorDefs.map((s, idx) => (
                                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr auto', gap: '8px', alignItems: 'center', marginBottom: '8px', background: '#fff', borderRadius: '8px', padding: '10px 12px', border: '1px solid #e5e7eb' }}>
                                    <input type="text" placeholder={L.sensorNamePh} value={s.name}
                                        onChange={e => updateSensorDef(idx, 'name', e.target.value)} style={inputStyle} />
                                    <input type="number" placeholder={L.latPh} value={s.lat} step="any"
                                        onChange={e => updateSensorDef(idx, 'lat', e.target.value)} style={inputStyle} />
                                    <input type="number" placeholder={L.lngPh} value={s.lng} step="any"
                                        onChange={e => updateSensorDef(idx, 'lng', e.target.value)} style={inputStyle} />
                                    <select value={s.fieldId || ''} onChange={e => updateSensorDef(idx, 'fieldId', e.target.value ? Number(e.target.value) : '')} style={inputStyle}>
                                        <option value="">{tr ? '— Tarla seç —' : '— Select field —'}</option>
                                        {fields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                    </select>
                                    {sensorDefs.length > 1 && (
                                        <button type="button" onClick={() => removeSensorDef(idx)}
                                            style={{ background: '#fee2e2', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', color: '#c00', fontSize: '14px' }}>✕</button>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button type="submit" style={btnGreen}>{L.createBtn}</button>
                            <button type="button" onClick={() => setShowForm(false)} style={btnSecondary}>{L.cancelBtn}</button>
                        </div>
                    </form>
                </div>
            )}

            {/* Keys list */}
            {loading ? (
                <p style={{ color: '#888' }}>{L.loading}</p>
            ) : keys.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 20px', color: '#9ca3af' }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔑</div>
                    <p>{L.empty}</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {keys.map(k => (
                        <div key={k.id} style={{ background: '#fff', border: `1px solid ${k.isActive ? '#bbf7d0' : '#fecaca'}`, borderRadius: '12px', padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
                                        <span style={{ fontWeight: 700, fontSize: '15px' }}>{k.name}</span>
                                        <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: k.isActive ? '#d1fae5' : '#fee2e2', color: k.isActive ? '#065f46' : '#991b1b' }}>
                                            {k.isActive ? L.active : L.inactive}
                                        </span>
                                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>{(k.sensors || []).length} {L.sensors}</span>
                                    </div>
                                    {k.description && <p style={{ margin: '0 0 10px', color: '#6b7280', fontSize: '13px' }}>{k.description}</p>}

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f4f4f4', borderRadius: '8px', padding: '8px 12px', fontFamily: 'monospace', fontSize: '13px', marginBottom: '10px' }}>
                                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {visibleKeys[k.id] ? k.key : maskKey(k.key)}
                                        </span>
                                        <button onClick={() => setVisibleKeys(prev => ({ ...prev, [k.id]: !prev[k.id] }))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>
                                            {visibleKeys[k.id] ? '🙈' : '👁️'}
                                        </button>
                                        <button onClick={() => copy(k.key, `key-${k.id}`)} style={{ background: copiedText === `key-${k.id}` ? '#2d7a2d' : '#555', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                            {copiedText === `key-${k.id}` ? '✓' : tr ? 'Kopyala' : 'Copy'}
                                        </button>
                                    </div>

                                    {(k.sensors || []).length > 0 && (
                                        <div style={{ marginBottom: '10px' }}>
                                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>{L.linked}</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                {k.sensors.map(s => (
                                                    <div key={s.id}>
                                                        {editingSensor?.sensor?.id === s.id ? (
                                                            <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '8px', padding: '10px 12px', marginBottom: '4px' }}>
                                                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr', gap: '6px', marginBottom: '8px' }}>
                                                                    <input type="text" value={editSensorForm.name} onChange={e => setEditSensorForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
                                                                    <input type="number" value={editSensorForm.lat} step="any" onChange={e => setEditSensorForm(f => ({ ...f, lat: e.target.value }))} style={inputStyle} />
                                                                    <input type="number" value={editSensorForm.lng} step="any" onChange={e => setEditSensorForm(f => ({ ...f, lng: e.target.value }))} style={inputStyle} />
                                                                    <select value={editSensorForm.fieldId || ''} onChange={e => setEditSensorForm(f => ({ ...f, fieldId: e.target.value ? Number(e.target.value) : '' }))} style={inputStyle}>
                                                                        <option value="">{tr ? '— Tarla seç —' : '— Select field —'}</option>
                                                                        {fields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                                                    </select>
                                                                </div>
                                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                                    <button onClick={handleEditSensor} style={{ ...btnGreen, padding: '6px 14px', fontSize: '12px' }}>{L.saveBtn}</button>
                                                                    <button onClick={() => setEditingSensor(null)} style={{ ...btnSecondary, padding: '6px 12px', fontSize: '12px' }}>{L.cancelBtn}</button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8f9fa', borderRadius: '6px', padding: '6px 10px', fontSize: '13px', flexWrap: 'wrap', marginBottom: '4px' }}>
                                                                <span style={{ background: '#2d7a2d', color: '#fff', borderRadius: '4px', padding: '1px 7px', fontWeight: 700, fontSize: '12px' }}>#{s.id}</span>
                                                                <span style={{ fontWeight: 600 }}>{s.name}</span>
                                                                <span style={{ color: '#888', fontSize: '12px' }}>{s.type}</span>
                                                                <span style={{ color: '#888', fontSize: '12px' }}>📍 {Number(s.lat).toFixed(4)}, {Number(s.lng).toFixed(4)}</span>
                                                                <button onClick={() => copy(String(s.id), `sid-${s.id}`)} style={{ background: copiedText === `sid-${s.id}` ? '#2d7a2d' : '#e2e8f0', color: copiedText === `sid-${s.id}` ? '#fff' : '#333', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }}>
                                                                    {copiedText === `sid-${s.id}` ? '✓' : L.copyId}
                                                                </button>
                                                                <button onClick={() => { setEditingSensor({ keyId: k.id, sensor: s }); setEditSensorForm({ name: s.name, type: s.type, lat: s.lat, lng: s.lng, fieldId: s.fieldId || '' }); }} style={{ background: '#fff3cd', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, color: '#856404', marginLeft: 'auto' }}>
                                                                    {L.editBtn}
                                                                </button>
                                                                <button onClick={() => handleDeleteSensor(k.id, s)} style={{ background: '#fee2e2', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, color: '#991b1b' }}>
                                                                    {L.deleteBtn}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {addingSensorToKey === k.id ? (
                                        <div style={{ background: '#eff6ff', borderRadius: '8px', padding: '12px', marginBottom: '8px', border: '1px solid #bfdbfe' }}>
                                            <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>{tr ? 'Sensör Ekle' : 'Add Sensor'}</div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr', gap: '8px', marginBottom: '8px' }}>
                                                <input type="text" placeholder={tr ? 'Sensör adı' : 'Sensor name'} value={addSensorForm.name} onChange={e => setAddSensorForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
                                                <input type="number" placeholder={tr ? 'Enlem' : 'Latitude'} value={addSensorForm.lat} step="any" onChange={e => setAddSensorForm(f => ({ ...f, lat: e.target.value }))} style={inputStyle} />
                                                <input type="number" placeholder={tr ? 'Boylam' : 'Longitude'} value={addSensorForm.lng} step="any" onChange={e => setAddSensorForm(f => ({ ...f, lng: e.target.value }))} style={inputStyle} />
                                                <select value={addSensorForm.fieldId || ''} onChange={e => setAddSensorForm(f => ({ ...f, fieldId: e.target.value ? Number(e.target.value) : '' }))} style={inputStyle}>
                                                    <option value="">{tr ? '— Tarla seç —' : '— Select field —'}</option>
                                                    {fields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                                </select>
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button onClick={() => handleAddSensor(k.id)} style={{ ...btnGreen, padding: '7px 16px', fontSize: '13px' }}>{tr ? 'Kaydet' : 'Save'}</button>
                                                <button onClick={() => { setAddingSensorToKey(null); setAddSensorForm({ ...emptySensor(), fieldId: defaultFieldId }); }} style={{ ...btnSecondary, padding: '7px 14px', fontSize: '13px' }}>{tr ? 'İptal' : 'Cancel'}</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button onClick={() => setAddingSensorToKey(k.id)}
                                            style={{ background: '#e8f5e9', border: '1px dashed #a5d6a7', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px', color: '#2e7d32', fontWeight: 600, marginBottom: '8px' }}>
                                            {L.addMore}
                                        </button>
                                    )}

                                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#9ca3af' }}>
                                        <span>{L.created} {fmt(k.createdAt)}</span>
                                        <span>{L.lastUsed} {fmt(k.lastUsedAt)}</span>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                                    <button onClick={() => handleToggle(k)} style={{ background: k.isActive ? '#fff3cd' : '#d1fae5', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: k.isActive ? '#856404' : '#065f46' }}>
                                        {k.isActive ? L.pause : L.activate}
                                    </button>
                                    <button onClick={() => handleRotate(k.id, k.name)} style={{ background: '#e2e8f0', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                                        {L.rotate}
                                    </button>
                                    <button onClick={() => handleDelete(k.id, k.name)} style={{ background: '#fee2e2', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#991b1b' }}>
                                        {L.deleteKey}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Integration guide */}
            <div style={{ marginTop: '32px' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700 }}>
                    📋 {tr ? 'Entegrasyon Kılavuzu' : 'Integration Guide'}
                </h3>

                {/* Single data send */}
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        <span style={{ background: '#2563eb', color: '#fff', borderRadius: '6px', padding: '2px 10px', fontWeight: 700, fontSize: '12px', fontFamily: 'monospace' }}>POST</span>
                        <span style={{ fontWeight: 700, fontSize: '14px' }}>{tr ? 'Tekli Veri Gönderimi' : 'Single Data Submission'}</span>
                    </div>
                    <div style={{ background: '#1e293b', borderRadius: '8px', padding: '12px 14px', fontFamily: 'monospace', fontSize: '12px', color: '#e2e8f0', marginBottom: '10px', overflowX: 'auto' }}>
                        <pre style={{ margin: 0 }}>{`POST http://169.58.21.41/api/ingest/sensor-data
X-API-Key: <your_key>
Content-Type: application/json

{
  "sensorId": 12,
  "havaSicaklik":  24.5,
  "havaNem":       65.2,
  "toprakNem":     38.1,
  "toprakSicaklik": 19.3,
  "yagis":         0.0,
  "source":        "external"
}`}
                        </pre>
                    </div>
                    <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#374151', fontWeight: 600 }}>{tr ? 'Alan Açıklamaları:' : 'Field Reference:'}</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: '12px', color: '#4b5563' }}>
                        <div><code style={{ color: '#2563eb' }}>sensorId</code> — {tr ? 'Sensör ID (zorunlu)' : 'Sensor ID (required)'}</div>
                        <div><code style={{ color: '#2563eb' }}>havaSicaklik</code> — {tr ? 'Hava sıcaklığı (°C)' : 'Air temperature (°C)'}</div>
                        <div><code style={{ color: '#2563eb' }}>havaNem</code> — {tr ? 'Hava nemi (%)' : 'Air humidity (%)'}</div>
                        <div><code style={{ color: '#2563eb' }}>toprakNem</code> — {tr ? 'Toprak nemi (%)' : 'Soil moisture (%)'}</div>
                        <div><code style={{ color: '#2563eb' }}>toprakSicaklik</code> — {tr ? 'Toprak sıcaklığı (°C)' : 'Soil temperature (°C)'}</div>
                        <div><code style={{ color: '#2563eb' }}>yagis</code> — {tr ? 'Yağış (mm)' : 'Precipitation (mm)'}</div>
                        <div><code style={{ color: '#2563eb' }}>source</code> — <span style={{ fontStyle: 'italic' }}>"external"</span> ({tr ? 'sabit değer' : 'fixed value'})</div>
                    </div>
                    <div style={{ marginTop: '10px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: '6px', padding: '8px 12px', fontSize: '12px', color: '#166534' }}>
                        ✅ {tr ? 'Başarı yanıtı:' : 'Success response:'} <code>{'{ "ok": true, "id": 4821 }'}</code>
                    </div>
                </div>

                {/* Bulk data send */}
                <div style={{ background: '#fdf4ff', border: '1px solid #e9d5ff', borderRadius: '12px', padding: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        <span style={{ background: '#7c3aed', color: '#fff', borderRadius: '6px', padding: '2px 10px', fontWeight: 700, fontSize: '12px', fontFamily: 'monospace' }}>POST</span>
                        <span style={{ fontWeight: 700, fontSize: '14px' }}>{tr ? 'Toplu Veri Gönderimi' : 'Bulk Data Submission'}</span>
                    </div>
                    <div style={{ background: '#1e293b', borderRadius: '8px', padding: '12px 14px', fontFamily: 'monospace', fontSize: '12px', color: '#e2e8f0', marginBottom: '10px', overflowX: 'auto' }}>
                        <pre style={{ margin: 0 }}>{`POST http://169.58.21.41/api/ingest/batch
X-API-Key: <your_key>
Content-Type: application/json

[
  {
    "sensorId": 12,
    "havaSicaklik":  24.5,
    "havaNem":       65.2,
    "toprakNem":     38.1,
    "source":        "external"
  },
  {
    "sensorId": 13,
    "havaSicaklik":  22.1,
    "toprakSicaklik": 18.7,
    "yagis":         1.2,
    "source":        "external"
  }
]`}
                        </pre>
                    </div>
                    <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#374151' }}>
                        {tr
                            ? 'Dizideki her nesne aynı alanları kullanır. Farklı sensörler tek istekte gönderilebilir.'
                            : 'Each object in the array uses the same fields. Different sensors can be sent in a single request.'}
                    </p>
                    <div style={{ marginTop: '6px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: '6px', padding: '8px 12px', fontSize: '12px', color: '#166534' }}>
                        ✅ {tr ? 'Başarı yanıtı:' : 'Success response:'} <code>{'{ "ok": true, "inserted": 2, "errors": [] }'}</code>
                    </div>
                </div>

                {/* Python example */}
                <details style={{ marginTop: '14px' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '13px', color: '#4f46e5', padding: '8px 0' }}>
                        🐍 {tr ? 'Python örnek kodu' : 'Python example'}
                    </summary>
                    <div style={{ background: '#1e293b', borderRadius: '8px', padding: '14px', fontFamily: 'monospace', fontSize: '12px', color: '#e2e8f0', marginTop: '8px', overflowX: 'auto' }}>
                        <pre style={{ margin: 0 }}>{`import requests

API_KEY = "<your_key>"
BASE    = "http://169.58.21.41/api/ingest"
HEADERS = {"X-API-Key": API_KEY, "Content-Type": "application/json"}

# ── Single record ──
requests.post(f"{BASE}/sensor-data", headers=HEADERS, json={
    "sensorId": 12,
    "havaSicaklik": 24.5,
    "havaNem": 65.2,
    "toprakNem": 38.1,
    "source": "external"
})

# ── Bulk (multiple sensors at once) ──
requests.post(f"{BASE}/batch", headers=HEADERS, json=[
    {"sensorId": 12, "havaSicaklik": 24.5, "havaNem": 65.2, "source": "external"},
    {"sensorId": 13, "havaSicaklik": 22.1, "yagis": 1.2,   "source": "external"},
])`}
                        </pre>
                    </div>
                </details>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ROOT COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const ApiKeyManager = ({ onSensorsChanged, fieldId, fields = [] }) => {
    const { language } = useLanguage();
    const tr = language !== 'en';
    const [mode, setMode] = useState('swamed');
    const [provider, setProvider] = useState('insighio');

    // DB-backed insighio sensors — loaded at parent level so mode switches don't lose data
    const [dbSensors, setDbSensors] = useState([]);
    const [dbLoading, setDbLoading] = useState(true);

    const loadDbSensors = async () => {
        setDbLoading(true);
        try {
            const res = await fetch(`${API_URL}/sensors`, { headers: getHeaders() });
            if (!res.ok) throw new Error(`${res.status}`);
            const all = await res.json();
            setDbSensors(all.filter(s => s.insighioDeviceId));
        } catch {
            // non-critical, list stays empty
        } finally {
            setDbLoading(false);
        }
    };

    useEffect(() => { loadDbSensors(); }, []);

    return (
        <div style={{ padding: '24px', maxWidth: '1040px' }}>
            {/* Page header */}
            <div style={{ marginBottom: '24px' }}>
                <h2 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: 700 }}>
                    {tr ? '🔌 API Entegrasyon Yönetimi' : '🔌 API Integration Management'}
                </h2>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
                    {tr
                        ? 'Entegrasyon türünü seçin: SWAMED API sağlayıcısı olarak çalışabilir, veya harici bir platformun verilerini çekebilirsiniz.'
                        : 'Choose your integration type: act as a SWAMED API provider, or pull data from an external platform.'}
                </p>
            </div>

            {/* Mode selector */}
            <ModeSelector mode={mode} onChange={setMode} tr={tr} />

            {/* Divider */}
            <div style={{ height: '1px', background: '#e5e7eb', marginBottom: '24px' }} />

            {/* Active mode panel */}
            {mode === 'swamed' ? (
                <SwamEdApiMode tr={tr} onSensorsChanged={onSensorsChanged} fields={fields} defaultFieldId={fieldId || ''} />
            ) : (
                <>
                    <div role="tablist" style={{ display: 'inline-flex', gap: 4, background: '#f1f5f9', borderRadius: 10, padding: 4, marginBottom: 20 }}>
                        {[{ id: 'insighio', label: 'insigh.io' }, { id: 'fieldclimate', label: 'FieldClimate' }].map(p => (
                            <button
                                key={p.id}
                                role="tab"
                                aria-selected={provider === p.id}
                                onClick={() => setProvider(p.id)}
                                style={{
                                    border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                                    background: provider === p.id ? '#fff' : 'transparent',
                                    color: provider === p.id ? '#4338ca' : '#64748b',
                                    boxShadow: provider === p.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                }}
                            >{p.label}</button>
                        ))}
                    </div>
                    {provider === 'insighio' ? (
                        <ExternalApiMode
                            tr={tr}
                            fieldId={fieldId}
                            onSensorsChanged={onSensorsChanged}
                            dbSensors={dbSensors}
                            dbLoading={dbLoading}
                            reloadDbSensors={loadDbSensors}
                        />
                    ) : (
                        <FieldClimatePanel tr={tr} fields={fields} defaultFieldId={fieldId} onSensorsChanged={onSensorsChanged} />
                    )}
                </>
            )}
        </div>
    );
};

export default ApiKeyManager;
